import { Routes, Route, useParams } from "react-router";
import { useEffect, useState } from "react";

const WEBSITE_URL = import.meta.env.VITE_WEBSITE_STATAMIC_URL;

function ExternalRedirect({ to }: { to: string }) {
	useEffect(() => {
		window.location.href = to;
	}, [to]);
	return null;
}
import { ToastProvider } from "./contexts/toast-context";
import { PageLoaderProvider } from "./contexts/page-loader-context";
import { ThemeProvider, brandkitToTheme } from "./contexts/theme-context";
import { FeedbackFlow } from "./modules/feedback";
import { useFormConfigStore } from "./modules/feedback/stores/form-config-store";
import { useGoogleFonts } from "./modules/feedback/hooks/use-google-fonts";
import NotFound from "./components/ui/not-found";

function FormFeedbackPage() {
	const { formSlug } = useParams<{ formSlug: string }>();
	const {
		loadConfig,
		loadBrand,
		loadBrandkit,
		brandkit,
		brand,
		config,
		isLoading,
		error,
		getIsDarkMode,
	} = useFormConfigStore();
	const [initialized, setInitialized] = useState(false);

	// Redirect to main site if formSlug is empty
	useEffect(() => {
		if (!formSlug || formSlug.trim() === "") {
			window.location.href = WEBSITE_URL;
		}
	}, [formSlug]);

	useEffect(() => {
		if (formSlug && !initialized) {
			async function initialize(formSlug: string) {
				await loadConfig(formSlug);
				const brand = await loadBrand(formSlug);
				await loadBrandkit(brand.slug);

				setInitialized(true);
			}

			initialize(formSlug);
		}
	}, [formSlug, loadConfig, initialized]);

	// Set document title when form is loaded
	useEffect(() => {
		if (config && brand) {
			document.title = `${config.title} - ${brand.name}`;
		}
	}, [config, brand]);

	// Set favicon from brandkit when available, otherwise fallback to default
	useEffect(() => {
		const faviconUrl = brandkit?.favicon_url || "/favicon.ico";
		const link =
			document.querySelector<HTMLLinkElement>("link[rel~='icon']");
		if (link) {
			link.href = faviconUrl;
			return;
		}
		const newLink = document.createElement("link");
		newLink.rel = "icon";
		newLink.href = faviconUrl;
		document.head.appendChild(newLink);
	}, [brandkit]);

	useGoogleFonts(
		brandkit
			? [
					{
						name: brandkit.font_heading_name,
						weight: brandkit.font_heading_weight,
					},
					{
						name: brandkit.font_body_name,
						weight: [brandkit.font_body_weight, "500"]
							.filter(Boolean)
							.join(";"),
					},
				]
			: [],
	);

	// Show loading state (always light mode to prevent flash)
	if (isLoading || (!initialized && !error)) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gray-50">
				<div className="text-center">
					<div className="w-8 h-8 border-2 rounded-full animate-spin mx-auto mb-3 border-gray-300 border-t-gray-600" />
					<p className="text-gray-600">Loading form...</p>
				</div>
			</div>
		);
	}

	// Show error state
	if (error || !config) {
		return <NotFound />;
	}

	const theme = brandkit ? brandkitToTheme(brandkit) : undefined;
	const isDark = getIsDarkMode();

	return (
		<ThemeProvider theme={theme}>
			<main className={`min-h-screen ${isDark ? "bg-gray-950" : ""}`}>
				<FeedbackFlow />
			</main>
		</ThemeProvider>
	);
}

export default function App() {
	return (
		<PageLoaderProvider>
			<ToastProvider>
				<Routes>
					{/* Redirect root to main site */}
					<Route
						path="/"
						element={<ExternalRedirect to={WEBSITE_URL} />}
					/>

					{/* Form-based feedback route: /f/{form_id} */}
					<Route path="/f/:formSlug" element={<FormFeedbackPage />} />

					{/* Redirect /f/ without slug to main site */}
					<Route
						path="/f"
						element={<ExternalRedirect to={WEBSITE_URL} />}
					/>

					{/* Catch all - show not found */}
					<Route path="*" element={<NotFound />} />
				</Routes>
			</ToastProvider>
		</PageLoaderProvider>
	);
}
